import { useAtom } from '@reatom/npm-react'
import styled from 'styled-components'

import { Numeric, Tooltip } from '@blade/ui-kit'
import { PairMigrationStatus } from '@root/entities/pair/model'
import {
  PairAuditBadge,
  PoolLiquidityBadge,
} from '@root/entities/pair/ui/common'
import { PairLiquidityFormatted } from '@root/entities/pair/ui/common/pair-liquidity-formatted'
import { showAllTimeHighChartLineAtom } from '@root/features/trade/trading'
import {
  formatMarketCapShort,
  MarkerCapShortFormatted,
} from '@root/shared/core/format'
import { useTranslation } from '@root/shared/localization'
import { Separator } from '@root/shared/ui-kit'

import { PairInformationBondingCurve } from './pair-information-b-curve'
import { PairInformationFees } from './pair-information-fees-paid'
import { PairInformationPoolItem } from './pair-information-pool-item'

import type { PairModel } from '@root/entities/pair/atoms/reatom-pair'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  pair: PairModel
  onAuditClick?: () => void
  withAudit?: boolean
  withAuditChecks?: boolean
  withPrice?: boolean
  withFees?: boolean
} & ClassNameProps

export const PairInformationPool: React.FC<Props> = ({
  pair,
  onAuditClick,
  withAudit = false,
  withAuditChecks = false,
  withPrice = false,
  withFees = false,
  className,
}) => {
  const { t } = useTranslation()
  const [price] = useAtom(pair.price)
  const [marketCapUsd] = useAtom(pair.marketCapUsd)
  const [supply] = useAtom(pair.supply)
  const [athMarketCap] = useAtom(pair.athMarketCap)
  const [audit] = useAtom(pair.audit)
  const [_, setShowAllTimeHighChartLine] = useAtom(showAllTimeHighChartLineAtom)

  const [shouldShowBondingCurve] = useAtom(
    (ctx) => {
      const migrationStatus = ctx.spy(pair.migrationStatus)
      return (
        migrationStatus instanceof PairMigrationStatus.Bonding ||
        migrationStatus instanceof PairMigrationStatus.MigratedTo
      )
    },
    [pair],
  )

  const booleanToYesNo = (bool: boolean) =>
    bool ? t('common.yes') : t('common.no')

  return (
    <Root className={className}>
      <Tooltip
        content={t('common.ath_mkt_cap', {
          value: formatMarketCapShort(athMarketCap),
        })}
      >
        <Mcap
          onMouseEnter={() => setShowAllTimeHighChartLine(true)}
          onMouseLeave={() => setShowAllTimeHighChartLine(false)}
        >
          <MarkerCapShortFormatted mcap={marketCapUsd} />
        </Mcap>
      </Tooltip>
      <Separator vertical />
      {withPrice ? (
        <>
          <PairInformationPoolItem title={t('common.price')}>
            <Numeric
              value={price.priceUsd}
              preset="currency-amount-short"
              currency="$"
            />
          </PairInformationPoolItem>
          <Separator vertical />
        </>
      ) : undefined}
      <PairInformationPoolItem title={t('common.liquidity')}>
        <PoolLiquidityBadge data={audit.checks.lpBurned} />
        <LiquidityFormatted pair={pair} />
      </PairInformationPoolItem>
      <Separator vertical />
      {withFees ? (
        <>
          <PairInformationFees pair={pair} />
          <Separator vertical />
        </>
      ) : undefined}
      {withAuditChecks ? (
        <>
          <PairInformationPoolItem title={t('trade.pair-information.freeze')}>
            <YesNo data-safe={audit.checks.isFreezable.status === 'Safe'}>
              {booleanToYesNo(audit.checks.isFreezable.value)}
            </YesNo>
          </PairInformationPoolItem>
          <Separator vertical />
          <PairInformationPoolItem
            tooltip={t('trading.audit.is_mintable.hint')}
            title={t('trade.pair-information.mint')}
          >
            <YesNo data-safe={audit.checks.isMintable.status === 'Safe'}>
              {booleanToYesNo(audit.checks.isMintable.value)}
            </YesNo>
          </PairInformationPoolItem>
          <Separator vertical />
        </>
      ) : undefined}
      <PairInformationPoolItem title={t('common.supply')}>
        <Numeric value={supply} preset="currency-amount-short" />
      </PairInformationPoolItem>
      {shouldShowBondingCurve ? (
        <>
          <Separator vertical />
          <PairInformationBondingCurve pair={pair} />
        </>
      ) : undefined}
      {withAudit ? (
        <>
          <Separator vertical />
          <PairInformationPoolItem title={t('common.audit')}>
            <PairAuditBadge audit={audit} onAuditClick={onAuditClick} />
          </PairInformationPoolItem>
        </>
      ) : undefined}
    </Root>
  )
}

const Root = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: auto;
  justify-self: end;
  gap: var(--pool-item-gap, 6px);
`

const YesNo = styled.div`
  &[data-safe='true'] {
    color: var(--success-9);
  }
  &[data-safe='false'] {
    color: var(--error-9);
  }
  font-size: 11px;
  font-weight: 600;
  line-height: 12px;
`

const LiquidityFormatted = styled(PairLiquidityFormatted)`
  min-width: 6ch;
`

const Mcap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white);

  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  white-space: nowrap;
`
